/* istanbul ignore file */
/**
 * Copyright Warner Bros. Entertainment, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Warner Bros. Entertainment, Inc. and its suppliers, if any.
 * The intellectual and technical concepts contained herein are
 * proprietary to Warner Bros. Entertainment, Inc. and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in process,
 * and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * unlawful and strictly forbidden unless prior written permission is
 * obtained from Warner Bros. Entertainment, Inc.
 */

// Controls how the asset name and displayName fields are autopopulated when adding a station document (See STUDIO-9969 for business logic)
const STATION_DOC_ASSET_NAMING = {
    SELECTED_MONTH: 'selected_month', // Set the fields to the run date month
    DEFAULT: 'default', // set the fields to match the selected content type name
    SELECTED_DAY_OF_WEEK: 'selected_day_of_week', // Sets the fields to the run date weekday name,'
    WEEKEND: 'weekend', // Sets the fields to string "Weekend"
};

const CONSTANTS = {
    CONTENT_TYPES: {
        INTERNAL_FILE: {
            id: 4,
            name: 'Internal File',
            subType: {
                PRODUCT_REPORT: {id: 12, name: 'Product Report', documentSuperType: 4},
                LOCAL_ACQUISITIONS_CO_PRODUCTIONS: {id: 13, name: 'Local Acquisitions & Co-Productions', documentSuperType: 4},
                AIR_DATE_SCHEDULES: {id: 14, name: 'Air Dates Schedules', documentSuperType: 4},
                THEATRICAL_GRID: {id: 27, name: 'Theatrical Grid', documentSuperType: 4}
            }
        },
        MARKETING_SAMPLE: {
            id: 5,
            name: 'Marketing Sample',
            subType: {
                VIDEO_SAMPLE: {id: 15, name: 'Video Sample', documentSuperType: 5},
                ONLINE_CAMPAIGN: {id: 16, name: 'Online Campaign', documentSuperType: 5},
                PRESS_AND_PUBLICITY: {id: 17, name: 'Press and Publicity', documentSuperType: 5},
                PRINT_CAMPAIGN: {id: 18, name: 'Print Campaign', documentSuperType: 5},
                PROMOTION_OR_CONTEST: {id: 19, name: 'Promotion or Contest', documentSuperType: 5},
                TRANSCRIPT: {id: 20, name: 'Transcript', documentSuperType: 5},
                WB_PRESENTATION: {id: 21, name: 'WB Presentation', documentSuperType: 5},
                MISCELLANEOUS: {id: 22, name: 'Miscellaneous', documentSuperType: 5}
            }
        },
        MARKETING_TOOL_KIT: {
            id: 6,
            name: 'Marketing Tool Kit',
            subType: {
                AFFILIATE_TOOL_KIT: {id: 23, name: 'Affiliate Tool Kit', documentSuperType: 6},
                ASSET_GUIDE: {id: 24, name: 'Asset Guide', documentSuperType: 6},
                PRODUCTION_GUIDE: {id: 25, name: 'Production Guide', documentSuperType: 6},
                MARKETING_GUIDE: {id: 26, name: 'Marketing Guide', documentSuperType: 6}
            }
        },
        OTHER: {
            id: 2,
            name: 'Other',
            subType: {
                OTHER_DOCUMENT: {id: 3, name: 'Other Document', documentSuperType: 2},
                TRIVIA: {id: 4, name: 'Trivia', documentSuperType: 2},
                CREDITS: {id: 5, name: 'Credits', documentSuperType: 2},
                LANGUAGE_AVAILABILITY_CHART: {id: 29, name: 'Language Availability Chart', documentSuperType: 2},
                PRIF: {id: 28, name: 'PRIF', documentSuperType: 2},
                EDIT_NOTES: {id: 30, name: 'Edit Notes', documentSuperType: 2}
            }
        },
        PRESS_FILE: {
            id: 1,
            name: 'Press File',
            subType: {
                PRESS_KIT: {id: 1, name: 'Press Kit', documentSuperType: 1},
                PRESS_RELEASE: {id: 2, name: 'Press Release', documentSuperType: 1}
            }
        },
        RESEARCH_FILE: {
            id: 3,
            name: 'Research File',
            subType: {
                RATINGS_FLASH: {id: 6, name: 'Ratings Flash', documentSuperType: 3},
                US_RATINGS: {id: 7, name: 'U.S. Ratings', documentSuperType: 3},
                US_PRIMETIME_SCHEDULE: {id: 8, name: 'U.S. Primetime Schedule', documentSuperType: 3},
                US_ADVERTISERS: {id: 9, name: 'U.S. Advertisers', documentSuperType: 3},
                PITCHES: {id: 10, name: 'Pitches', documentSuperType: 3},
                INTERNATIONAL_RESEARCH: {id: 11, name: 'International Research', documentSuperType: 3}
            }
        },
        STATION_DOCUMENT: {
            id: 7,
            name: 'Station Document',
            subType: {
                STA_PROMOTIONS: {
                    altId: 'stationAirDateSchedules',
                    altName: 'Station Promotion',
                    assetNaming: STATION_DOC_ASSET_NAMING.DEFAULT,
                    cmsOrder: 12,
                    documentSuperType: 7,
                    id: 32,
                    name: 'Promotion',
                    order: 11,
                    subscriptionTypeId: 9,
                },
                STA_SEASON_PACKETS: {
                    altId: 'stationSeasonPackets',
                    altName: 'Station Season Packet',
                    assetNaming: STATION_DOC_ASSET_NAMING.DEFAULT,
                    cmsOrder: 6,
                    documentSuperType: 7,
                    id: 33,
                    name: 'Season Packet',
                    order: 12,
                    subscriptionTypeId: 10,
                },
                STA_AIR_SCHEDULES: {
                    altId: 'stationAirSchedules',
                    altName: 'Station Air Schedule',
                    assetNaming: STATION_DOC_ASSET_NAMING.SELECTED_MONTH,
                    cmsOrder: 0,
                    documentSuperType: 7,
                    id: 34,
                    name: 'Air Schedule',
                    order: 0,
                    subscriptionTypeId: 11,
                },
                STA_CONTACT_SHEETS: {
                    altId: 'stationContactSheets',
                    altName: 'Station Contact Sheet',
                    assetNaming: STATION_DOC_ASSET_NAMING.DEFAULT,
                    cmsOrder: 9,
                    documentSuperType: 7,
                    id: 35,
                    name: 'Contact Sheet',
                    order: 13,
                    subscriptionTypeId: 12,
                },
                STA_REFEEDS: {
                    altId: 'stationRefeeds',
                    altName: 'Station Redelivery',
                    assetNaming: STATION_DOC_ASSET_NAMING.DEFAULT,
                    cmsOrder: 7,
                    documentSuperType: 7,
                    id: 36,
                    name: 'Redelivery',
                    order: 14,
                    subscriptionTypeId: 13,
                },
                STA_CASH_FORMATS: {
                    altId: 'stationCashFormats',
                    altName: 'Station Cash Format',
                    assetNaming: STATION_DOC_ASSET_NAMING.DEFAULT,
                    cmsOrder: 8,
                    documentSuperType: 7,
                    id: 38,
                    name: 'Cash Format',
                    order: 10,
                    subscriptionTypeId: 15,
                },
                STA_TITLE_LISTS: {
                    altId: 'stationTitleLists',
                    altName: 'Station Title List',
                    assetNaming: STATION_DOC_ASSET_NAMING.DEFAULT,
                    cmsOrder: 14,
                    documentSuperType: 7,
                    id: 39,
                    name: 'Title List',
                    order: 7,
                    subscriptionTypeId: 16,
                },
                STA_SHOW_INFO: {
                    altId: 'stationShowInfo',
                    altName: 'Station Show Info',
                    assetNaming: STATION_DOC_ASSET_NAMING.DEFAULT,
                    cmsOrder: 13,
                    documentSuperType: 7,
                    id: 40,
                    name: 'Show Info',
                    order: 8,
                    subscriptionTypeId: 17,
                },
                STA_PRODUCTION_TIMING_SHEETS: {
                    altId: 'stationProductionTimingSheets',
                    altName: 'Station Production Timing Sheet',
                    assetNaming: STATION_DOC_ASSET_NAMING.DEFAULT,
                    cmsOrder: 11,
                    documentSuperType: 7,
                    id: 41,
                    name: 'Production Timing Sheet',
                    order: 9,
                    subscriptionTypeId: 18,
                },
                STA_FORMAT_SHEET_PRIMARY_RUN: {
                    altId: 'stationFormatSheetPrimaryRun',
                    altName: 'Station Format Sheet - Primary Run',
                    assetNaming: STATION_DOC_ASSET_NAMING.SELECTED_DAY_OF_WEEK,
                    cmsOrder: 1,
                    documentSuperType: 7,
                    id: 42,
                    name: 'Format Sheet - Primary Run',
                    order: 1,
                    subscriptionTypeId: 19,
                },
                STA_FORMAT_SHEET_SECONDAY_RUN: {
                    altId: 'stationFormatSheetSecondaryRun',
                    altName: 'Station Format Sheet - Secondary Run',
                    assetNaming: STATION_DOC_ASSET_NAMING.SELECTED_DAY_OF_WEEK,
                    cmsOrder: 2,
                    documentSuperType: 7,
                    id: 43,
                    name: 'Format Sheet - Secondary Run',
                    order: 2,
                    subscriptionTypeId: 20,
                },
                STA_FORMAT_SHEET_TERTIARY_RUN: {
                    altId: 'stationFormatSheetTertiaryRun',
                    altName: 'Station Format Sheet - Tertiary Run',
                    assetNaming: STATION_DOC_ASSET_NAMING.SELECTED_DAY_OF_WEEK,
                    cmsOrder: 3,
                    documentSuperType: 7,
                    id: 44,
                    name: 'Format Sheet - Tertiary Run',
                    order: 3,
                    subscriptionTypeId: 21,
                },
                STA_FORMAT_SHEET_DAY_DELAY: {
                    altId: 'stationFormatSheetDayDelay',
                    altName: 'Station Format Sheet - Day Delay',
                    assetNaming: STATION_DOC_ASSET_NAMING.SELECTED_DAY_OF_WEEK,
                    cmsOrder: 10,
                    documentSuperType: 7,
                    id: 45,
                    name: 'Format Sheet - Day Delay',
                    order: 4,
                    subscriptionTypeId: 22,
                },
                STA_FORMAT_SHEET_WEEKEND_PRIMARY_RUN: {
                    altId: 'stationFormatSheetWeekendPrimaryRun',
                    altName: 'Station Format Sheet - Weekend Primary Run',
                    assetNaming: STATION_DOC_ASSET_NAMING.WEEKEND,
                    cmsOrder: 4,
                    documentSuperType: 7,
                    id: 46,
                    name: 'Format Sheet - Weekend Primary Run',
                    order: 5,
                    subscriptionTypeId: 23,
                },
                STA_FORMAT_SHEET_WEEKEND_SECONDARY_RUN: {
                    altId: 'stationFormatSheetWeekendSecondaryRun',
                    altName: 'Station Format Sheet - Weekend Secondary Run',
                    assetNaming: STATION_DOC_ASSET_NAMING.WEEKEND,
                    cmsOrder: 5,
                    documentSuperType: 7,
                    id: 47,
                    name: 'Format Sheet - Weekend Secondary Run',
                    order: 6,
                    subscriptionTypeId: 24,
                }
            },
        },
    },

    toArray: function(constant) {
        return Object.keys(this[constant])
            .map(k => this[constant][k])
            .sort((a, b) => a.name.localeCompare(b.name));
    }
};

CONSTANTS.isStationDocumentRunDateAllowed = (documentTypeId) => {
    const formatSheets = Object.keys(CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType)
        .filter(
            k => CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType[k].name.includes('Format Sheet')
        ).reduce((r, k) => {
            r.push(parseInt(CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType[k].id, 10));
            return r;
        }, []);
    // STUDIO-3030 formatRunDate field should appear for any format sheet type and air schedules
    return documentTypeId === CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType.STA_AIR_SCHEDULES.id || formatSheets.includes(documentTypeId);
};

CONSTANTS.CONTENT_TYPE_MAP = Object.keys(CONSTANTS.CONTENT_TYPES).reduce( (o, k) => {
    o = Object.keys(CONSTANTS.CONTENT_TYPES[k].subType).reduce( (o1, k1) => {
        let item = CONSTANTS.CONTENT_TYPES[k].subType[k1];
        o1[item.id] = item;
        return o1;
    }, o);
    return o;
}, {});

export const STATIONS_SUB_TYPES = Object.keys(CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType)
    .map(key => CONSTANTS?.CONTENT_TYPES.STATION_DOCUMENT.subType[key].id);

export const STATIONS_FORMAT_SHEETS = Object.keys(CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType)
    .filter(key => CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType[key].name.includes('Format Sheet'))
    .map(key => CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType[key].id);
export const DOCUMENT_TYPES = (() => {
    const documentTypes = {};

    for (const contentTypeKey in CONSTANTS.CONTENT_TYPES) {
        const subTypes = CONSTANTS.CONTENT_TYPES[contentTypeKey].subType;
        for (const subTypeKey in subTypes) {
            const subTypeId = subTypes[subTypeKey].id;
            const documentType = CONSTANTS.CONTENT_TYPES[contentTypeKey].name;
            documentTypes[subTypeId] = documentType;
        }
    }

    return documentTypes;
})();

export const DOCUMENT_CT_NAME_BY_ID = (() => {
    const documentContentTypes = {};

    for (const contentTypeKey in CONSTANTS.CONTENT_TYPES) {
        const subTypes = CONSTANTS.CONTENT_TYPES[contentTypeKey].subType;
        for (const subTypeKey in subTypes) {
            const subTypeId = subTypes[subTypeKey].id;
            const documentSubTypeName = subTypes[subTypeKey].name;
            documentContentTypes[subTypeId] = documentSubTypeName;
        }
    }

    return documentContentTypes;
})();

export const SUBSCRIPTION_DOCUMENT_TYPES = Object.keys(CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType)
    .map(key => {
        const subType = CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType[key];
        return {
            cmsOrder: subType.cmsOrder,
            contentTypeId: subType.subscriptionTypeId,
            id: subType.altId || key,
            name: subType.altName || subType.name,
        };
    })
    .sort((a, b) => a.cmsOrder - b.cmsOrder);
export const STATION_SUBSCRIPTION_CT_TYPES = (() => {
    const stationSubscriptionContentTypes = {
        ALL_VIDEOS: {id: 1, name: 'Video Subscriptions', stationDocument: false},
        FULL_EPISODES: {id: 2, name: 'Screeners', stationDocument: false},
        VAM_DELIVERY: {id: 3, name: 'VAM Orders', stationDocument: false},
        EPISODIC_DELIVERY: {id: 4, name: 'Episodics', stationDocument: false},
        IMAGES: {id: 5, name: 'Image Subscriptions', stationDocument: false},
        FAVORITES: {id: 6, name: 'Favorites', stationDocument: false},
        METADATA: {id: 7, name: 'Metadata', stationDocument: false},
    };

    for (const key in CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType) {
        const subType = CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType[key];
        stationSubscriptionContentTypes[key] = {
            id: subType.subscriptionTypeId,
            name: subType.altName,
            stationDocument: true,
        };
    }

    return stationSubscriptionContentTypes;
})();

export const STATION_SUBSCRIPTION_CT_TYPES_MAP = (() => {
    const stationSubscriptionContentTypesMap = {
        1: 'All Videos',
        2: 'Full Episodes',
        3: 'VAM Delivery',
        4: 'Episodic Delivery',
        5: 'Images',
        6: 'Favorites',
        7: 'Metadata',
    };

    for (const key in CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType) {
        const subType = CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType[key];
        stationSubscriptionContentTypesMap[subType.subscriptionTypeId] = subType.altName;
    }

    return stationSubscriptionContentTypesMap;
})();

export const STATION_TITLE_ACTIONS_MAP = (() => {
    const stationTitleActionsMap = {
        allVideos: 1,
        fullEpisodes: 2,
        vamDelivery: 3,
        episodicDelivery: 4,
        images: 5,
        favorites: 6,
        metadata: 7,
    };

    for (const key in CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType) {
        const subType = CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType[key];
        stationTitleActionsMap[subType.altId] = subType.subscriptionTypeId;
    }

    return stationTitleActionsMap;
})();

export const displayOrder = Object.keys(CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType)
    .map(key => CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType[key])
    .sort((a, b) => a.cmsOrder - b.cmsOrder)
    .map(item => item.id);

export const displayOrderName = (() => {
    const displayOrderNameObj = {};

    Object.values(CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType).forEach((subType) => {
        displayOrderNameObj[subType.name] = subType.id;
    });

    return displayOrderNameObj;
})();

export const stationCatNamesById = (() => {
    const stationCatNamesByIdObj = {};

    Object.values(CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT.subType).forEach((subType) => {
        stationCatNamesByIdObj[subType.name] = subType.id;
    });

    return stationCatNamesByIdObj;
})();

export const stationCatNames = (() => {
    return {
        'Air Schedule': 'Air Schedule',
        'Cash Format': 'Cash Format',
        'Contact Sheet': 'Contact Sheet',
        'Format Sheet - Day Delay': 'Format Sheet - Day Delay',
        'Format Sheet - Primary Run': 'Format Sheet - Primary Run',
        'Format Sheet - Secondary Run': 'Format Sheet - Secondary Run',
        'Format Sheet - Tertiary Run': 'Format Sheet - Tertiary Run',
        'Format Sheet - Weekend Primary Run': 'Format Sheet - Weekend Primary Run',
        'Format Sheet - Weekend Secondary Run': 'Format Sheet - Weekend Secondary Run',
        'Production Timing Sheet': 'Production Timing Sheet',
        'Promotion': 'Promotion',
        'Redelivery': 'Redelivery',
        'Season Packet': 'Season Packet',
        'Show Info': 'Show Info',
        'Title List': 'Title List'
    };
})();

export const isStation = item => STATIONS_SUB_TYPES.includes(item?.contentType) || item?.documentSuperType === 7;

export const findCmsOrderById = (id) => {
    const stationDocument = CONSTANTS.CONTENT_TYPES.STATION_DOCUMENT;
    for (const subTypeKey in stationDocument.subType) {
        const subType = stationDocument.subType[subTypeKey];
        if (subType.id === id && subType.cmsOrder !== undefined) {
            return subType.cmsOrder;
        }
    }
    return null;
};

export const ANALYTICS_SUBSCRIPTION_CONTENT_TYPE_BY_ID = (() => {
    return {
        1: 'ALL_VIDEOS',
        2: 'FULL_EPISODES',
        3: 'VAM_DELIVERY',
        4: 'EPISODIC_DELIVERY',
        5: 'IMAGES',
        6: 'FAVORITES',
        7: 'METADATA',
        9: 'STA_PROMOTIONS',
        10: 'STA_SEASON_PACKETS',
        11: 'STA_AIR_SCHEDULES',
        12: 'STA_CONTACT_SHEETS',
        13: 'STA_REFEEDS',
        15: 'STA_CASH_FORMATS',
        16: 'STA_TITLE_LISTS',
        17: 'STA_SHOW_INFO',
        18: 'STA_PRODUCTION_TIMING_SHEETS',
        19: 'STA_FORMAT_SHEET_PRIMARY_RUN',
        20: 'STA_FORMAT_SHEET_SECONDAY_RUN',
        21: 'STA_FORMAT_SHEET_TERTIARY_RUN',
        22: 'STA_FORMAT_SHEET_DAY_DELAY',
        23: 'STA_FORMAT_SHEET_WEEKEND_PRIMARY_RUN',
        24: 'STA_FORMAT_SHEET_WEEKEND_SECONDARY_RUN',
    };
})();

export {
    CONSTANTS as DOCUMENT_CT_TYPES,
    STATION_DOC_ASSET_NAMING
};
